import {
	Component,
	ElementRef,
	EventEmitter,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatStepper,
	MatStepperIcon,
	MatStep,
	MatStepLabel,
} from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { AppDialogComponent } from 'src/app/components/app-dialog/app-dialog.component';
import {
	SelectedUserEvent,
	UserType,
} from 'src/app/components/user-search-bar/user-search-bar.component';
import { Company } from 'src/app/models/data/company';
import { SystemCapabilityItem } from 'src/app/models/data/systemCapability';
import { User } from 'src/app/models/data/user';
import { UserProfile } from 'src/app/models/model';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { BannerMessageService } from 'src/app/services/banner-message.service';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { AddRoleDefineComponent } from './add-role-define/add-role-define.component';
import { AddRoleUsersComponent } from './add-role-users/add-role-users.component';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { UserExistsSnackbarComponent } from '../../../components/user-exists-snackbar/user-exists-snackbar.component';
import { AppNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AddRoleConfirmationComponent } from './add-role-confirmation/add-role-confirmation.component';

enum StepperPage {
	define = 0,
	addUser = 1,
	confirmation = 2,
}

@Component({
	selector: 'app-add-role',
	templateUrl: './add-role-main-tab.component.html',
	styleUrls: [
		'./add-role-main-tab.component.scss',
		'../../common/stepper.scss',
	],
	imports: [
		AppNavigationHeaderComponent,
		MatButton,
		MatStepper,
		MatStepperIcon,
		MatIcon,
		MatStep,
		MatStepLabel,
		MatProgressSpinner,
		AddRoleDefineComponent,
		AddRoleUsersComponent,
		AddRoleConfirmationComponent,
	],
})
export class AddRoleComponent implements OnInit {
	@Output() createRoleRequestStatusEvent =
		new EventEmitter<CreateRoleRequestStatusEvent>();

	@ViewChild(AddRoleDefineComponent) addRoleDefine?: AddRoleDefineComponent;
	@ViewChild(AddRoleUsersComponent) addRoleUsers?: AddRoleUsersComponent;
	@ViewChild('stepperRef') stepperRef!: ElementRef;
	snackBarRef?: MatSnackBarRef<UserExistsSnackbarComponent>;

	editRole: EditRole = {
		id: '',
		name: '',
		systemCapabilityItems: [],
		users: [],
	};
	company?: Company;
	isActiveCompany = false;
	isEditable = false;
	serverCallInProgress = false;

	constructor(
		private readonly apiService: ApiService,
		private readonly route: ActivatedRoute,
		private readonly dialog: MatDialog,
		private readonly router: Router,
		private readonly profileService: UserProfileService,
		private readonly navHistory: NavigationHistoryService,
		private readonly userProfileService: UserProfileService,
		private readonly bannerService: BannerMessageService,
		private snackBar: MatSnackBar
	) {
		this.company = this.router.lastSuccessfulNavigation?.extras.state?.company;
	}

	async ngOnInit(): Promise<void> {
		if (!this.company) {
			this.isActiveCompany = true;
			this.company = await this.getCompany();
		}
	}

	movePrevious(stepper: MatStepper): void {
		if (stepper.selectedIndex === StepperPage.define) {
			this.navHistory.navigateBack();
		} else {
			stepper.previous();
		}
	}

	async moveNext(stepper: MatStepper): Promise<void> {
		if (stepper.selectedIndex === StepperPage.define) {
			stepper.next();
		} else {
			const users = await Promise.all(
				this.editRole?.users?.map(async (user) => {
					if (!user?.user?.id) {
						const email = user?.user?.email?.trim()?.toLowerCase();
						const queryParameters: QueryParam[] = [
							{ param: 'email', value: email },
							{ param: 'rowIndex', value: 0 },
							{ param: 'rowCount', value: 100 },
						];
						const fetchedUsers =
							await this.apiService.getAllUsers(queryParameters);
						return !!fetchedUsers?.find(
							(usr) => usr?.email?.trim()?.toLowerCase() === email
						);
					}
					return false;
				})
			);
			if (users?.some(Boolean)) {
				this.snackBarRef = this.snackBar.openFromComponent(
					UserExistsSnackbarComponent,
					{
						duration: 3000,
						panelClass: 'existing-user-snackbar',
					}
				);
				return;
			}

			this.addRoleUsers?.checkForExistingRolesThenContinue(() => {
				this.createCustomRole();
				stepper.next();
			}, this.company?.id);
		}
	}

	isValid(stepper: MatStepper): boolean {
		switch (stepper.selectedIndex) {
			case StepperPage.define:
				return this.addRoleDefine?.isValid ?? false;
			case StepperPage.addUser:
				return this.addRoleUsers?.isValid ?? false;
		}
		return false;
	}

	async getCompany(): Promise<Company | undefined> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		const companyId = userProfile?.activeRoleDetails?.company.id;

		if (!companyId) {
			console.error('No company id for the active role!');
			return undefined;
		}

		// tslint:disable:no-non-null-assertion CompanyID already checked for null;
		const activeCompany = await this.apiService.getCompanyById(companyId!);

		return activeCompany.company;
	}

	async createCustomRole(): Promise<void> {
		// make sure the user can't create the record multiple times from clicking the button multiple times quickly
		if (!this.serverCallInProgress) {
			this.serverCallInProgress = true;

			this.createRoleRequestStatusEvent.emit({
				status: CreateRoleRequestStatus.loading,
			});
			const companyId = this.company?.id;
			if (!companyId) {
				console.error('No company id or custom role event!');
				return;
			}

			try {
				const role = await this.apiService.createNewRole(
					companyId,
					this.editRole.name,
					this.editRole.systemCapabilityItems
				);

				const userEvents: SelectedUserEvent[] = this.editRole.users ?? [];
				const users = userEvents.map((it) => it.user);
				const userIds = users.filter((it) => !!it.id).map((it) => it.id);

				if (userIds.length > 0) {
					await this.apiService.assignRoleToUserId(userIds, role.id, companyId);
					if (
						userIds.includes(
							(await this.profileService.getUserProfile()).user.id
						)
					) {
						this.profileService.clearUserProfile();
						await this.profileService.getUserProfile();
					}
				}

				const usersToCreate = userEvents
					.filter((it) => it.userType === UserType.newUser)
					.map((it) => it.user);
				if (usersToCreate.length > 0) {
					const newUserResponse = await this.apiService.registerNewUser(
						usersToCreate,
						companyId,
						role.id
					);
					const createdUserIds: string[] = newUserResponse.data.userIds;
					userIds.push(...createdUserIds);
				}

				this.createRoleRequestStatusEvent.emit({
					status: CreateRoleRequestStatus.success,
					data: { roleName: role.name, users },
				});
			} catch (err: any) {
				const data = {
					title: err.errors[0]?.title,
					message: err.errors[0]?.detail,
				};
				this.dialog.open(AppDialogComponent, { data });

				const roleName = this.editRole.name ?? '';

				this.createRoleRequestStatusEvent.emit({
					status: CreateRoleRequestStatus.failure,
					data: { roleName, users: [] },
				});
			}

			this.serverCallInProgress = false;
		}
	}
}

export enum CreateRoleRequestStatus {
	loading,
	success,
	failure,
}

export interface CreateRoleRequestStatusEvent {
	status: CreateRoleRequestStatus;
	data?: { roleName?: string; users: User[] };
}

export interface EditRole {
	id: '';
	name: string;
	systemCapabilityItems: SystemCapabilityItem[];
	users: SelectedUserEvent[];
}
